import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, AlertTriangle, Info } from 'lucide-react';

/**
 * Real-Time Validation Component
 * Provides instant feedback on field values with smart error messages
 * Auto-dismisses after 15 seconds, or can be manually closed
 */
export default function CalculatorValidation({ 
  scenarioInfo,
  volumeAssumptions,
  proposedPricing,
  transactionDistribution,
  showValidation = true
}) {
  const [validationResults, setValidationResults] = useState({
    errors: [],
    warnings: [],
    info: []
  });
  const [isDismissed, setIsDismissed] = useState(false);
  const [dismissedItems, setDismissedItems] = useState(new Set());

  useEffect(() => {
    if (!showValidation) return;

    const errors = [];
    const warnings = [];
    const info = [];

    // Volume Validation
    if (volumeAssumptions.totalTransactions > 0) {
      const paymentMixTotal = 
        (volumeAssumptions.achPercent || 0) +
        (volumeAssumptions.ccPercent || 0) +
        (volumeAssumptions.amexPercent || 0) +
        (volumeAssumptions.debitPercent || 0);

      if (Math.abs(paymentMixTotal - 100) > 0.01 && paymentMixTotal > 0) {
        errors.push({
          field: 'Payment Mix',
          message: `Payment mix totals ${paymentMixTotal.toFixed(2)}% - must equal 100%`,
          suggestion: 'Adjust percentages to sum to exactly 100%'
        });
      } else if (paymentMixTotal === 100) {
        info.push({
          field: 'Payment Mix',
          message: 'Payment mix validated ✓',
          type: 'success'
        });
      }

      // Check for unrealistic values
      if (volumeAssumptions.totalPayments > 50000) {
        warnings.push({
          field: 'Total Volume',
          message: `$${volumeAssumptions.totalPayments * 1000000} annual volume is very high`,
          suggestion: 'Verify this is entered in thousands (1000 = $1M)'
        });
      }

      if (volumeAssumptions.totalTransactions > 1000000) {
        warnings.push({
          field: 'Transactions',
          message: `${volumeAssumptions.totalTransactions.toLocaleString()} transactions/year is unusually high`,
          suggestion: 'Typical range: 10,000 - 500,000 transactions'
        });
      }
    }

    // Pricing Validation
    if (proposedPricing.paymentRates) {
      // Check for negative margins
      Object.entries(proposedPricing.paymentRates).forEach(([type, rates]) => {
        if (rates.takeRate < 0.5) {
          warnings.push({
            field: `${type} Take Rate`,
            message: `${rates.takeRate}% is very low - may result in negative margins`,
            suggestion: 'Typical minimum: 2.0% for sustainable profitability'
          });
        }

        if (rates.takeRate > 5.0) {
          warnings.push({
            field: `${type} Take Rate`,
            message: `${rates.takeRate}% is high - may not be competitive`,
            suggestion: 'Industry standard: 2.0-3.5% for most payment types'
          });
        }
      });
    }

    // Transaction Distribution Validation
    if (transactionDistribution && transactionDistribution.length > 0) {
      const distTotal = transactionDistribution.reduce((sum, row) => sum + (parseFloat(row.currentPercent) || 0), 0);
      
      if (Math.abs(distTotal - 100) > 0.01 && distTotal > 0) {
        errors.push({
          field: 'Distribution',
          message: `Transaction distribution totals ${distTotal.toFixed(2)}% - must equal 100%`,
          suggestion: 'Adjust percentages in Block 5 to sum to 100%'
        });
      } else if (distTotal === 100) {
        info.push({
          field: 'Distribution',
          message: 'Transaction distribution validated ✓',
          type: 'success'
        });
      }
    }

    // Required fields check
    if (!scenarioInfo.municipality) {
      warnings.push({
        field: 'Municipality',
        message: 'Municipality is required for calculation',
        suggestion: 'Search or manually enter a municipality name'
      });
    }

    if (!scenarioInfo.serviceId && !scenarioInfo.service) {
      warnings.push({
        field: 'Service',
        message: 'Service selection is required',
        suggestion: 'Select a service type from the dropdown'
      });
    }

    setValidationResults({ errors, warnings, info });
  }, [scenarioInfo, volumeAssumptions, proposedPricing, transactionDistribution, showValidation]);

  // Auto-dismiss non-error notifications after 15 seconds
  useEffect(() => {
    if (validationResults.warnings.length > 0 || validationResults.info.length > 0) {
      const timer = setTimeout(() => {
        setIsDismissed(true);
      }, 15000); // 15 seconds

      return () => clearTimeout(timer);
    }
  }, [validationResults]);

  // Reset dismissed state when validation changes
  useEffect(() => {
    setIsDismissed(false);
  }, [validationResults.errors.length, validationResults.warnings.length]);

  const handleDismissItem = (type, idx) => {
    const key = `${type}-${idx}`;
    setDismissedItems(prev => new Set([...prev, key]));
  };

  const handleDismissAll = () => {
    setIsDismissed(true);
  };

  if (!showValidation || isDismissed || 
      (validationResults.errors.length === 0 && 
       validationResults.warnings.length === 0 && 
       validationResults.info.length === 0)) {
    return null;
  }

  return (
    <div className="fixed top-20 right-4 z-30 w-96 space-y-2 max-h-[calc(100vh-6rem)] overflow-y-auto">
      {/* Dismiss All Button */}
      {(validationResults.warnings.length > 0 || validationResults.info.length > 0) && (
        <div className="flex justify-end mb-2">
          <button
            onClick={handleDismissAll}
            className="text-xs text-slate-500 hover:text-slate-700 bg-white px-3 py-1 rounded-full shadow-sm border border-slate-200 hover:border-slate-300 transition-all"
          >
            Dismiss All (auto-closes in 15s)
          </button>
        </div>
      )}

      {/* Errors */}
      {validationResults.errors.map((error, idx) => {
        const key = `error-${idx}`;
        if (dismissedItems.has(key)) return null;
        
        return (
          <div key={key} className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 shadow-lg animate-slide-in-right">
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <h4 className="text-sm font-semibold text-red-900">{error.field}</h4>
                  <div className="flex items-center gap-2">
                    <span className="text-xs bg-red-600 text-white px-2 py-0.5 rounded-full">Error</span>
                    <button
                      onClick={() => handleDismissItem('error', idx)}
                      className="text-red-400 hover:text-red-600 text-lg leading-none"
                      aria-label="Dismiss"
                    >
                      ×
                    </button>
                  </div>
                </div>
                <p className="text-sm text-red-800 mb-2">{error.message}</p>
                <p className="text-xs text-red-700 italic">💡 {error.suggestion}</p>
              </div>
            </div>
          </div>
        );
      })}

      {/* Warnings */}
      {validationResults.warnings.map((warning, idx) => {
        const key = `warning-${idx}`;
        if (dismissedItems.has(key)) return null;
        
        return (
          <div key={key} className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-4 shadow-lg animate-slide-in-right">
            <div className="flex gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <h4 className="text-sm font-semibold text-amber-900">{warning.field}</h4>
                  <div className="flex items-center gap-2">
                    <span className="text-xs bg-amber-600 text-white px-2 py-0.5 rounded-full">Warning</span>
                    <button
                      onClick={() => handleDismissItem('warning', idx)}
                      className="text-amber-400 hover:text-amber-600 text-lg leading-none"
                      aria-label="Dismiss"
                    >
                      ×
                    </button>
                  </div>
                </div>
                <p className="text-sm text-amber-800 mb-2">{warning.message}</p>
                <p className="text-xs text-amber-700 italic">💡 {warning.suggestion}</p>
              </div>
            </div>
          </div>
        );
      })}

      {/* Info/Success */}
      {validationResults.info.map((item, idx) => {
        const key = `info-${idx}`;
        if (dismissedItems.has(key)) return null;
        
        return (
          <div key={key} className="bg-green-50 border-l-4 border-green-500 rounded-lg p-3 shadow-lg animate-slide-in-right">
            <div className="flex gap-2 items-center justify-between">
              <div className="flex gap-2 items-center">
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                <p className="text-sm text-green-800 font-medium">{item.message}</p>
              </div>
              <button
                onClick={() => handleDismissItem('info', idx)}
                className="text-green-400 hover:text-green-600 text-lg leading-none"
                aria-label="Dismiss"
              >
                ×
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Add animation styles
const styles = `
@keyframes slide-in-right {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.animate-slide-in-right {
  animation: slide-in-right 0.3s ease-out;
}
`;

if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}
